import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '@/apiFetch';

/**
 * useTextToSpeech — premium-first TTS with browser fallback.
 *
 * Path 1 (default): POST the text to `/v1/soteria/tts` and play the
 * MP3 the backend returns. The backend uses Google Cloud TTS with
 * Chirp 3 HD `en-US-Chirp3-HD-Aoede` — a genuinely soothing,
 * smooth female neural voice that doesn't depend on what the user's
 * OS has installed. This is the path you actually want for demos.
 *
 * Path 2 (fallback): if the backend 5xxs (Cloud TTS API not enabled,
 * quota exhausted, network blip) we fall through to the browser's
 * SpeechSynthesis API with the best neural voice we can find. Quality
 * varies by OS but the button always works.
 *
 * Either way the public surface is the same: speak / pause / resume /
 * stop, with isSpeaking / isPaused / supported flags. The caller
 * never has to know which engine ran.
 */
export interface UseTextToSpeechResult {
  supported: boolean;
  /** True while the engine is actively voicing the supplied text. */
  isSpeaking: boolean;
  /** True while paused mid-playback. */
  isPaused: boolean;
  /** True while we're fetching audio from the backend (before playback starts). */
  isLoading: boolean;
  speak: (text: string) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
}

// ── Web Speech fallback helpers ────────────────────────────────────
function getSpeechSynthesis(): SpeechSynthesis | null {
  return typeof window !== 'undefined' && 'speechSynthesis' in window
    ? window.speechSynthesis
    : null;
}

function pickPremiumVoice(
  voices: SpeechSynthesisVoice[],
): SpeechSynthesisVoice | null {
  if (!voices.length) return null;
  const wantsFrench = navigator.language?.startsWith('fr');
  const primaryLang = wantsFrench ? 'fr' : 'en';

  const langScoped = voices.filter((v) =>
    v.lang.toLowerCase().startsWith(primaryLang),
  );
  const pool = langScoped.length ? langScoped : voices;

  // 1. Hand-curated female premium voices by exact name. These are
  //    actually-installed-and-actually-good across browsers.
  const PREFERRED_NAMES = [
    // macOS Premium
    'Ava (Premium)',
    'Ava (Enhanced)',
    'Allison (Premium)',
    'Allison (Enhanced)',
    'Samantha (Premium)',
    'Samantha (Enhanced)',
    'Susan (Premium)',
    'Susan (Enhanced)',
    // Chrome / Edge cloud voices
    'Google US English Female',
    'Microsoft Aria Online (Natural) - English (United States)',
    'Microsoft Jenny Online (Natural) - English (United States)',
    'Microsoft Aria',
    'Microsoft Jenny',
  ];
  for (const name of PREFERRED_NAMES) {
    const exact = pool.find((v) => v.name === name);
    if (exact) return exact;
  }

  // 2. Quality hints in the voice name.
  const PREMIUM_HINTS = [
    'neural',
    'natural',
    'wavenet',
    'studio',
    'premium',
    'enhanced',
  ];
  // 3. Lean toward feminine names if we have to guess.
  const FEMALE_HINTS = [
    'female',
    'samantha',
    'ava',
    'allison',
    'susan',
    'victoria',
    'aria',
    'jenny',
    'sara',
    'zira',
    'helena',
  ];

  const premiumFemale = pool.find(
    (v) =>
      PREMIUM_HINTS.some((h) => v.name.toLowerCase().includes(h)) &&
      FEMALE_HINTS.some((h) => v.name.toLowerCase().includes(h)),
  );
  if (premiumFemale) return premiumFemale;

  const premium = pool.find((v) =>
    PREMIUM_HINTS.some((h) => v.name.toLowerCase().includes(h)),
  );
  if (premium) return premium;

  const femalish = pool.find((v) =>
    FEMALE_HINTS.some((h) => v.name.toLowerCase().includes(h)),
  );
  if (femalish) return femalish;

  const defaultVoice = pool.find((v) => v.default);
  if (defaultVoice) return defaultVoice;

  return pool[0] ?? null;
}

export function useTextToSpeech(): UseTextToSpeechResult {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Backend playback uses HTMLAudioElement + an Object URL we own.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Web Speech fallback ref.
  const voiceRef = useRef<SpeechSynthesisVoice | null>(null);

  const supported = useRef<boolean>(
    typeof window !== 'undefined' &&
      ('AudioContext' in window || 'webkitAudioContext' in window),
  ).current;

  // Pre-resolve the best fallback voice.
  useEffect(() => {
    const synth = getSpeechSynthesis();
    if (!synth) return;
    const refresh = () => {
      voiceRef.current = pickPremiumVoice(synth.getVoices());
    };
    refresh();
    synth.addEventListener('voiceschanged', refresh);
    return () => synth.removeEventListener('voiceschanged', refresh);
  }, []);

  // Release the Object URL we minted for backend playback.
  const releaseAudioUrl = useCallback(() => {
    if (audioUrlRef.current) {
      try {
        URL.revokeObjectURL(audioUrlRef.current);
      } catch {
        /* ignore */
      }
      audioUrlRef.current = null;
    }
  }, []);

  const stopBackend = useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = '';
      } catch {
        /* ignore */
      }
      audioRef.current = null;
    }
    releaseAudioUrl();
  }, [releaseAudioUrl]);

  const stopWebSpeech = useCallback(() => {
    const synth = getSpeechSynthesis();
    if (!synth) return;
    try {
      synth.cancel();
    } catch {
      /* ignore */
    }
  }, []);

  const stop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    stopBackend();
    stopWebSpeech();
    setIsSpeaking(false);
    setIsPaused(false);
    setIsLoading(false);
  }, [stopBackend, stopWebSpeech]);

  // Web Speech fallback — used when the backend call fails.
  const speakWithWebSpeech = useCallback((text: string) => {
    const synth = getSpeechSynthesis();
    if (!synth) {
      setIsSpeaking(false);
      return;
    }
    synth.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    const v = voiceRef.current ?? pickPremiumVoice(synth.getVoices());
    if (v) {
      utt.voice = v;
      utt.lang = v.lang;
    }
    utt.rate = 0.95;
    utt.pitch = 1.0;
    utt.volume = 1.0;
    utt.onstart = () => {
      setIsSpeaking(true);
      setIsPaused(false);
    };
    utt.onpause = () => setIsPaused(true);
    utt.onresume = () => setIsPaused(false);
    utt.onend = () => {
      setIsSpeaking(false);
      setIsPaused(false);
    };
    utt.onerror = () => {
      setIsSpeaking(false);
      setIsPaused(false);
    };
    synth.speak(utt);
  }, []);

  const speak = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      // Cancel any in-flight session before starting fresh.
      stop();

      setIsLoading(true);
      const abort = new AbortController();
      abortRef.current = abort;

      apiFetch('/soteria/tts', {
        method: 'POST',
        signal: abort.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: trimmed }),
      })
        .then(async (res) => {
          if (!res.ok) throw new Error(`tts ${res.status}`);
          const blob = await res.blob();
          if (abort.signal.aborted) return;
          const url = URL.createObjectURL(blob);
          audioUrlRef.current = url;
          const audio = new Audio(url);
          audioRef.current = audio;
          audio.onplay = () => {
            setIsLoading(false);
            setIsSpeaking(true);
            setIsPaused(false);
          };
          audio.onpause = () => {
            // Don't flip isSpeaking on natural end — onended handles that.
            if (!audio.ended) setIsPaused(true);
          };
          audio.onplaying = () => setIsPaused(false);
          audio.onended = () => {
            setIsSpeaking(false);
            setIsPaused(false);
            releaseAudioUrl();
          };
          audio.onerror = () => {
            setIsSpeaking(false);
            setIsPaused(false);
            setIsLoading(false);
            releaseAudioUrl();
          };
          await audio.play();
        })
        .catch((err) => {
          if (abort.signal.aborted) return;
          setIsLoading(false);
          // Backend unavailable → degrade to browser Web Speech so the
          // button still does something.
          // eslint-disable-next-line no-console
          console.warn('[soteria-tts] backend failed, using web speech:', err);
          speakWithWebSpeech(trimmed);
        });
    },
    [releaseAudioUrl, speakWithWebSpeech, stop],
  );

  const pause = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      return;
    }
    const synth = getSpeechSynthesis();
    if (synth) {
      try {
        synth.pause();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const resume = useCallback(() => {
    if (audioRef.current && audioRef.current.paused) {
      void audioRef.current.play();
      return;
    }
    const synth = getSpeechSynthesis();
    if (synth) {
      try {
        synth.resume();
      } catch {
        /* ignore */
      }
    }
  }, []);

  // Clean up on unmount.
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      stopBackend();
      stopWebSpeech();
    };
  }, [stopBackend, stopWebSpeech]);

  return { supported, isSpeaking, isPaused, isLoading, speak, pause, resume, stop };
}
